import {useCallback, useEffect, type ReactNode} from 'react'
import {setTitle} from '@github-ui/document-metadata'
import {featureFlag, isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useCurrentQueryRoute} from '@github-ui/react-core/future/use-current-query-route'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {CopilotLicenseType} from '@github-ui/copilot-chat-types'
import {EntitlementProvider} from '@github-ui/copilot-entitlement/EntitlementContext'
import {CurrentRepositoryProvider} from '@github-ui/current-repository'
import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {CurrentUserProvider} from '@github-ui/agent-sessions/contexts/CurrentUserContext'
import {useInitializeAuthToken} from '@github-ui/agent-sessions/hooks/use-initialize-auth-token'
import {useSSOChangeDetection} from '@github-ui/agent-sessions/hooks/use-sso-change-detection'
import {useSessions} from '@github-ui/agent-sessions/hooks/useSessions'
import {maybeNormalizeSession} from '@github-ui/agent-sessions/types/api-task'
import {SsoOrganizationsContext} from '@github-ui/agent-sessions/contexts/ConversationAccessContext'
import {ConversationLocked} from '@github-ui/agent-sessions/components/conversation-access/ConversationLocked'
import {provisionalTaskTitleFromNavigationState} from '@github-ui/agent-sessions/utils/task-helpers'
import {
  CustomInstructionsProvider,
  useHasCustomInstructionsProvider,
} from '@github-ui/copilot-chat-app/custom-instructions/context'
import {getAgentsPath} from '@github-ui/agent-sessions/utils/constants'
import {Navigate, useLocation, useNavigate} from '@github-ui/react-router'
import {UNSUBMITTED_CHAT_MESSAGE, useHiddenEarlyTaskIds} from '@github-ui/agent-sessions/ahp/early-task-visibility'

import {AgentsSidebarLayout} from './AgentsSidebarLayout'
import {RegularTaskChat, TaskNameChangeContext} from './RegularTaskChat'
import {fetchCopilotChatPayload, type ChatPayload} from './copilot-chat-payload-route-query'
import {isLegacyQuickChatTask, quickChatTaskTitleDuringProvisioning} from '../utils/is-quick-chat-task'
import {setCopilotDocumentTitle} from '../utils/document-title'
import type {
  CopilotAgentsTaskPayload,
  AccessDeniedCopilotAgentsTaskPayload,
  NormalizedCopilotAgentsTaskPayload,
} from '../types/copilot-agents-task-payload'
import {usePersistentAgentsChatSession} from './PersistentAgentsChatHost'
import {AgentsTaskWorkbench} from './AgentsTaskWorkbench'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {Banner} from '@primer/react'
import {announce} from '@github-ui/aria-live'

export function CopilotAgentsTaskPage() {
  const currentRoute = useCurrentQueryRoute()
  const location = useLocation()
  const {data: payload, queryKey, dataUpdatedAt} = useRouteQuery(currentRoute, 'mainQuery')
  const {data: noticeDismissal} = useRouteQuery(currentRoute, 'noticeDismissal')
  const hiddenTaskIds = useHiddenEarlyTaskIds()
  const queryClient = useQueryClient()
  const oneCopilotEnabled = isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')
  const onTaskNameChange = useCallback(
    (name: string | undefined) => setCopilotDocumentTitle(queryClient, queryKey, name),
    [queryClient, queryKey],
  )
  const routePayload = payload as CopilotAgentsTaskPayload | AccessDeniedCopilotAgentsTaskPayload | undefined
  const accessDenied = !!routePayload && isAccessDeniedTaskPayload(routePayload)
  const taskId = routePayload
    ? isAccessDeniedTaskPayload(routePayload)
      ? routePayload.taskId
      : (routePayload.taskId ?? routePayload.task.id)
    : undefined
  const hiddenTask = !!taskId && hiddenTaskIds.has(taskId)
  useEffect(() => {
    if (oneCopilotEnabled && (accessDenied || hiddenTask)) {
      setCopilotDocumentTitle(queryClient, queryKey)
    }
  }, [oneCopilotEnabled, accessDenied, hiddenTask, queryClient, queryKey, dataUpdatedAt])

  if (!routePayload) return null
  if (hiddenTask) return <UnsubmittedChatNotice />
  if (isAccessDeniedTaskPayload(routePayload)) {
    return <AccessDeniedCopilotAgentsTaskPage key={routePayload.taskId} payload={routePayload} />
  }

  const typedPayload = routePayload
  const normalizedPayload: NormalizedCopilotAgentsTaskPayload = {
    ...typedPayload,
    sessions: typedPayload.sessions.map(maybeNormalizeSession),
  }
  if (isLegacyQuickChatTask(normalizedPayload.task)) {
    return <Navigate to={getAgentsPath()} replace />
  }

  return (
    <TaskNameChangeContext value={oneCopilotEnabled ? onTaskNameChange : undefined}>
      <SupportedCopilotAgentsTaskPage
        key={normalizedPayload.taskId ?? normalizedPayload.task.id}
        payload={normalizedPayload}
        cliRemoteNoticeDismissed={noticeDismissal?.dismissed}
        provisionalTaskTitle={provisionalTaskTitleFromNavigationState(location.state)}
      />
    </TaskNameChangeContext>
  )
}

function UnsubmittedChatNotice() {
  const navigate = useNavigate()
  useEffect(() => {
    announce(UNSUBMITTED_CHAT_MESSAGE, {assertive: true})
  }, [])
  return (
    <Banner
      variant="warning"
      title="Chat not submitted"
      description={UNSUBMITTED_CHAT_MESSAGE}
      primaryAction={<Banner.PrimaryAction onClick={() => navigate('/copilot')}>Start new chat</Banner.PrimaryAction>}
    />
  )
}

function isAccessDeniedTaskPayload(
  payload: CopilotAgentsTaskPayload | AccessDeniedCopilotAgentsTaskPayload,
): payload is AccessDeniedCopilotAgentsTaskPayload {
  return 'conversationAccessDenied' in payload && payload.conversationAccessDenied === true
}

function useCopilotChatPayloadQuery() {
  // This viewer-specific payload includes instructions and permission state, so it stays in memory rather than
  // persisting to IndexedDB. The legacy /github-copilot/chat endpoint also has no generated runtime schema.
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema
  return useQuery({
    queryKey: ['copilot-agents', 'chat-payload'],
    queryFn: fetchCopilotChatPayload,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

/**
 * The task page for a conversation Mission Control refused: the server sends no
 * task or sessions, so the transcript is replaced by the lock (with the SSO
 * re-prompt for any organization whose SSO lapsed) inside the agents sidebar.
 */
export function AccessDeniedCopilotAgentsTaskPage({payload}: {payload: AccessDeniedCopilotAgentsTaskPayload}) {
  const persistentChatSession = usePersistentAgentsChatSession()
  const {data: chatPayload, isError: chatPayloadFailed} = useCopilotChatPayloadQuery()
  useInitializeAuthToken(payload.initialAuthToken)
  useEffect(() => {
    if (!isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')) {
      setTitle('Agents · GitHub Copilot')
    }
  }, [])

  const activeTaskId = payload.taskId

  return (
    <CurrentUserProvider
      currentUser={payload.currentUser}
      currentUserIsSessionOwner={false}
      optedInToFeedback={payload.optedInToUserFeedback ?? false}
    >
      <ConsecutiveCAPI401sProvider>
        <AgentsSidebarLayout
          agentsEnabled={payload.agentsEnabled !== false}
          enterpriseSandboxEnabled={payload.enterpriseSandboxEnabled !== false}
          showSpacesNav={payload.spacesEnabled}
          showCustomizeNav={payload.customizeEnabled !== false}
          currentUserLogin={payload.currentUser?.login}
          currentUserId={payload.currentUser?.id}
          taskGroups={undefined}
          contentKey={payload.taskId ?? ''}
          legacyChatPayload={chatPayload}
          legacyChatPayloadPending={!chatPayload && !chatPayloadFailed}
          legacyChatPayloadFailed={chatPayloadFailed}
          fetchLegacyChatPayload={false}
          chatApiURL={chatPayload?.apiURL ?? persistentChatSession?.chatApiURL}
          activeTaskId={activeTaskId}
          sidebarProps={{
            activeTaskId,
            navigateToAgentsPage: true,
            activeTaskIdForDeleteRedirect: payload.taskId,
          }}
        >
          <SidebarPageLayout.Content as="div" padding="none" width="full">
            {!featureFlag.isFeatureEnabled('copilot_mission_control_sticky_header') && (
              <SidebarPageLayout.SidebarTrigger />
            )}
            <ConversationLocked
              surface="task"
              ssoOrganizations={chatPayload?.ssoOrganizations}
              requiresSignIn={!payload.currentUser}
              // The lock comes from the page load itself, so re-reading means reloading the page.
              onRetry={() => window.location.reload()}
            />
          </SidebarPageLayout.Content>
        </AgentsSidebarLayout>
      </ConsecutiveCAPI401sProvider>
    </CurrentUserProvider>
  )
}

function SupportedCopilotAgentsTaskPage({
  payload,
  cliRemoteNoticeDismissed,
  provisionalTaskTitle,
}: {
  payload: NormalizedCopilotAgentsTaskPayload
  cliRemoteNoticeDismissed?: boolean
  provisionalTaskTitle?: string
}) {
  const {data: chatPayload, isError: chatPayloadFailed} = useCopilotChatPayloadQuery()
  useEffect(() => {
    if (chatPayloadFailed && !chatPayload) {
      announce('Chat configuration could not be loaded. Reload the page to retry.', {assertive: true})
    }
  }, [chatPayload, chatPayloadFailed])

  const currentUserConfiguration = getCurrentUserConfiguration(payload, payload.sessions)

  return (
    <CurrentUserProvider {...currentUserConfiguration}>
      <ConsecutiveCAPI401sProvider>
        <ErrorBoundary key={payload.taskId ?? payload.task.id} boundaryName="CopilotAgentsTaskPageContent">
          {!chatPayload && chatPayloadFailed && (
            <Banner
              variant="critical"
              title="Chat configuration could not be loaded"
              description="Reload the page to retry."
            />
          )}
          <CopilotAgentsTaskPageContent
            payload={payload}
            chatPayload={chatPayload}
            cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
            provisionalTaskTitle={provisionalTaskTitle}
            chatPayloadPending={!chatPayload && !chatPayloadFailed}
            chatPayloadFailed={chatPayloadFailed}
          />
        </ErrorBoundary>
      </ConsecutiveCAPI401sProvider>
    </CurrentUserProvider>
  )
}

export function getCurrentUserConfiguration(
  payload: CopilotAgentsTaskPayload,
  sessions: NormalizedCopilotAgentsTaskPayload['sessions'] | undefined = payload.sessions.map(maybeNormalizeSession),
) {
  const {currentUser, sessionOwners} = payload

  return {
    currentUser,
    currentUserIsSessionOwner:
      !!currentUser &&
      (sessionOwners?.length
        ? sessionOwners.some(owner => owner.id === currentUser.id)
        : (sessions?.some(session => session.user_id === currentUser.id) ?? false)),
    optedInToFeedback: payload.optedInToUserFeedback,
  }
}

export function CopilotAgentsTaskPageContent({
  payload,
  chatPayload,
  cliRemoteNoticeDismissed,
  provisionalTaskTitle,
  chatPayloadPending = false,
  chatPayloadFailed = false,
}: {
  payload: NormalizedCopilotAgentsTaskPayload
  chatPayload?: ChatPayload
  cliRemoteNoticeDismissed?: boolean
  provisionalTaskTitle?: string
  chatPayloadPending?: boolean
  chatPayloadFailed?: boolean
}) {
  if (isLegacyQuickChatTask(payload.task)) {
    return <Navigate to={getAgentsPath()} replace />
  }

  return (
    // The task chat renders outside the sidebar's CopilotChatProvider, so hand
    // it the SSO organizations for the locked-conversation re-prompt directly.
    <SsoOrganizationsContext value={chatPayload?.ssoOrganizations}>
      <SupportedCopilotAgentsTaskPageContent
        key={payload.taskId ?? payload.task.id}
        payload={payload}
        chatPayload={chatPayload}
        cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
        provisionalTaskTitle={provisionalTaskTitle}
        chatPayloadPending={chatPayloadPending}
        chatPayloadFailed={chatPayloadFailed}
      />
    </SsoOrganizationsContext>
  )
}

function SupportedCopilotAgentsTaskPageContent({
  payload,
  chatPayload,
  cliRemoteNoticeDismissed,
  provisionalTaskTitle,
  chatPayloadPending,
  chatPayloadFailed,
}: {
  payload: NormalizedCopilotAgentsTaskPayload
  chatPayload?: ChatPayload
  cliRemoteNoticeDismissed?: boolean
  provisionalTaskTitle?: string
  chatPayloadPending: boolean
  chatPayloadFailed: boolean
}) {
  const persistentChatSession = usePersistentAgentsChatSession()
  useInitializeAuthToken(payload.initialAuthToken)
  useSSOChangeDetection(payload.initialAuthToken?.ssoOrgIDs)
  useEffect(() => {
    if (!isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')) {
      setTitle('Agents · GitHub Copilot')
    }
  }, [])

  if (!payload.task) throw new Error('Copilot Agents task payload is missing Mission Control task metadata.')

  const routedTaskId = payload.taskId ?? payload.task.id
  const provisioningPrompt =
    persistentChatSession?.provisioningTaskId === routedTaskId
      ? persistentChatSession.provisioningPrompt?.text
      : undefined
  const provisionalTitle = provisioningPrompt ?? provisionalTaskTitle
  const taskTitle = quickChatTaskTitleDuringProvisioning(payload.task.name, provisionalTitle)
  const displayedPayload =
    taskTitle === payload.task.name ? payload : {...payload, task: {...payload.task, name: taskTitle}}
  const taskTitleOverride =
    provisionalTitle && taskTitle && routedTaskId ? {taskId: routedTaskId, title: taskTitle} : undefined
  const configuration = getGlobalTaskChatConfiguration(payload, cliRemoteNoticeDismissed)
  const activeTaskId = payload.taskId ?? payload.task.id
  const taskContent = (
    <GlobalTaskChat
      payload={displayedPayload}
      chatPayload={chatPayload}
      chatPayloadPending={chatPayloadPending}
      configuration={configuration}
    >
      <RegularTaskChat
        payload={displayedPayload}
        automationsVisible={configuration.automationsVisible}
        deletionDestination={configuration.deletionDestination}
        cliRemoteNoticeDismissed={configuration.cliRemoteNoticeDismissed}
        repositoryContext={configuration.repositoryContext}
      />
    </GlobalTaskChat>
  )
  return (
    <AgentsSidebarLayout
      agentsEnabled={payload.agentsEnabled !== false}
      showSpacesNav={payload.spacesEnabled}
      showCustomizeNav={payload.customizeEnabled}
      currentUserLogin={payload.currentUser?.login}
      currentUserId={payload.currentUser?.id}
      enterpriseSandboxEnabled={payload.enterpriseSandboxEnabled !== false}
      taskGroups={payload.taskGroups}
      contentKey={payload.taskId ?? ''}
      legacyChatPayload={chatPayload}
      legacyChatPayloadPending={chatPayloadPending}
      legacyChatPayloadFailed={chatPayloadFailed}
      fetchLegacyChatPayload={false}
      chatApiURL={chatPayload?.apiURL ?? persistentChatSession?.chatApiURL}
      activeTaskId={activeTaskId}
      taskTitleOverride={taskTitleOverride}
      sidebarProps={{
        activeTaskId,
        navigateToAgentsPage: true,
        activeTaskIdForDeleteRedirect: payload.taskId,
      }}
    >
      <SidebarPageLayout.Content as="div" padding="none" width="full">
        {!featureFlag.isFeatureEnabled('copilot_mission_control_sticky_header') && <SidebarPageLayout.SidebarTrigger />}
        <AgentsTaskWorkbench
          payload={payload}
          repository={configuration.repositoryContext}
          previewUrl={payload.previewUrl ?? ''}
        >
          {taskContent}
        </AgentsTaskWorkbench>
      </SidebarPageLayout.Content>
    </AgentsSidebarLayout>
  )
}

/**
 * Repository and entitlement context for every task chat surface rendered on the
 * global page, so the regular task chat and the Quick Chat task view share one
 * provider stack.
 */
function GlobalTaskChat({
  payload,
  chatPayload,
  chatPayloadPending,
  configuration,
  children,
}: {
  payload: NormalizedCopilotAgentsTaskPayload
  chatPayload?: ChatPayload
  chatPayloadPending: boolean
  configuration: ReturnType<typeof getGlobalTaskChatConfiguration>
  children: ReactNode
}) {
  const hasParentInstructionsProvider = useHasCustomInstructionsProvider()
  const content = (
    <GlobalTaskChatContent payload={payload} configuration={configuration}>
      {children}
    </GlobalTaskChatContent>
  )
  const taskChat = !hasParentInstructionsProvider ? (
    <CustomInstructionsProvider
      configurationPending={chatPayloadPending}
      personalInstructions={chatPayload?.personalInstructions}
      customInstructions={chatPayload?.customInstructions}
      references={chatPayload?.refs}
      instructionScope={configuration.repositoryContext ? 'repository' : 'chat'}
    >
      {content}
    </CustomInstructionsProvider>
  ) : (
    content
  )

  return configuration.repositoryContext ? (
    <CurrentRepositoryProvider repository={configuration.repositoryContext.repository}>
      {taskChat}
    </CurrentRepositoryProvider>
  ) : (
    taskChat
  )
}

function GlobalTaskChatContent({
  payload,
  configuration,
  children,
}: {
  payload: NormalizedCopilotAgentsTaskPayload
  configuration: ReturnType<typeof getGlobalTaskChatConfiguration>
  children: ReactNode
}) {
  const {data: currentSessions} = useSessions({
    pullId: payload.pull?.id,
    taskId: payload.taskId,
    initialData: payload.sessions,
    sessionsPollingInterval: payload.sessionsPollingInterval ?? 0,
  })
  const currentUserConfiguration = getCurrentUserConfiguration(payload, currentSessions)

  return (
    <CurrentUserProvider {...currentUserConfiguration}>
      <EntitlementProvider
        initialLicenseType={configuration.initialLicenseType}
        hasCEorCBAccess={configuration.hasCEorCBAccess}
      >
        {children}
      </EntitlementProvider>
    </CurrentUserProvider>
  )
}

export function getGlobalTaskChatConfiguration(payload: CopilotAgentsTaskPayload, cliRemoteNoticeDismissed?: boolean) {
  const repositoryContext = payload.repository
    ? {
        repository: payload.repository,
        repoOwner: payload.repository.ownerLogin,
        repoName: payload.repository.name,
      }
    : undefined

  return {
    initialLicenseType: payload.hasPaidCopilotAccess
      ? CopilotLicenseType.LicensedFull
      : CopilotLicenseType.LicensedLimited,
    hasCEorCBAccess: payload.hasCEorCBAccess,
    automationsVisible: payload.automationsVisible ?? false,
    repositoryContext,
    deletionDestination: getAgentsPath(),
    cliRemoteNoticeDismissed,
  }
}
